import { FormEvent } from "react"
import styled from "styled-components"

interface Props {
  placeholder?: string
  label?: string
  errorMessage?: string
  value?: string
  name?: string
  id?: string
  rows?: number
  maxLength?: number
  skipLabel?: boolean
  showCharCount?: boolean
  onChange?: (event: FormEvent<HTMLTextAreaElement>) => void
}

const FieldWrapper = styled.div`
  width: 100%;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`

const FieldLabel = styled.div`
  font-size: 12px;
  margin-bottom: 8px;
`

const Field = styled.div`
  width: 100%;
  position: relative;
`

const StyledTextarea = styled.textarea<{ hasError?: boolean; showCharCount?: boolean }>`
  min-height: 60px;
  padding: 10px ${({ showCharCount }) => (showCharCount ? "50px" : "12px")} 10px 12px;
  width: 100%;
  font-size: 14px;
  font-family: Inter, sans-serif;
  border-radius: 10px;
  border: 1px solid ${({ hasError }) => (hasError ? "#ef4444" : "#e2e8f0")};
  resize: none;
  background: var(--surface-1);
  line-height: 1.5;
  overflow-y: auto;

  &::placeholder {
    font-size: 14px;
    font-family: Inter, sans-serif;
    color: var(--text-disabled);
  }

  &:hover {
    border: 1px solid #4ae9ff;
    box-shadow:
      0 0 0 4px rgb(205 252 255 / 70%),
      0 0 0 2px #fff;
  }

  &:focus,
  &:active {
    outline: none;
    border: 1px solid #4ae9ff;
    box-shadow:
      0 0 0 4px rgb(205 252 255 / 70%),
      0 0 0 2px #fff;
  }

  ${({ hasError }) =>
    hasError &&
    `
    &:hover {
      border-color: var(--status-danger);
      box-shadow: none;
    }

    &:focus,
    &:active {
      border-color: var(--status-danger);
      box-shadow: none;
    }
  `}
`

const CharCount = styled.div`
  position: absolute;
  bottom: 8px;
  right: 12px;
  font-size: 12px;
  color: var(--text-muted);
  background: var(--surface-1);
  padding: 2px 4px;
  pointer-events: none;
`

const ErrorMessage = styled.div`
  font-family: Inter, sans-serif;
  font-size: 14px;
  color: var(--status-danger);
  max-width: 75%;
  margin-top: 5px;
  padding-left: 20px;
  position: relative;

  &::before {
    content: url("assets/svg/icon-error-message.svg");
    position: absolute;
    left: 0;
    top: 0;
  }
`

export const Textarea = ({
  placeholder,
  label,
  errorMessage,
  value,
  name,
  id,
  rows,
  maxLength,
  skipLabel,
  showCharCount,
  onChange,
}: Props) => {
  const currentLength = value?.length || 0

  return (
    <FieldWrapper>
      {!skipLabel && label && <FieldLabel>{label}</FieldLabel>}
      <Field>
        <StyledTextarea
          hasError={!!errorMessage}
          showCharCount={showCharCount}
          placeholder={placeholder}
          value={value}
          name={name}
          id={id}
          rows={rows}
          maxLength={maxLength}
          onChange={onChange}
        />
        {showCharCount && maxLength && (
          <CharCount>
            {currentLength}/{maxLength}
          </CharCount>
        )}
      </Field>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </FieldWrapper>
  )
}
